import type { TimelineItem } from '@xxtract/ui';
import type { TFunction } from 'i18next';
import { formatOrderTimestamp } from './format';
import type { TimelineEvent } from './orders.api';

/**
 * fu-7: the canonical order-level process step sequence shown on the order-detail timeline
 * card. PACKSHOT is a documented, known-incomplete source (h3-5/KNOWN_SERVICE_CODES comment:
 * "PACKSHOT ~24.6%" coverage) — a missing PACKSHOT step is an explicit "bron publiceert dit nog
 * niet" gap rather than a plain "not started yet" future step.
 */
const CANONICAL_PROCESS_STEPS = ['INTAKE', 'PROCESSING', 'QUALITY', 'PACKSHOT', 'DELIVERY'] as const;
const NON_PUBLISHING_STEPS = new Set<string>(['PACKSHOT']);

/**
 * FR18: chronologische ordertijdlijn, nieuwste onderaan (EXPERIENCE.md). Events are the raw
 * procesManagement event log (interne weergave toont systeemtaal, FR48 — codes are not
 * translated). The most recent real event is `status: 'current'`, earlier ones `'complete'`.
 * Canonical steps with no event at all get an explicit trailing note: a known non-publishing
 * step (`'gap'`) or a step that simply hasn't started yet (`'future'`).
 */
export function toTimelineItems(events: TimelineEvent[], locale: string, t: TFunction): TimelineItem[] {
  const sorted = [...events].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  const seenProcessCodes = new Set(sorted.map((event) => event.processCode));

  const eventItems: TimelineItem[] = sorted.map((event, index) => ({
    id: String(event.id),
    label: `${event.processCode}/${event.eventCode}`,
    timestamp: formatOrderTimestamp(event.timestamp, locale),
    source: t('orders.timeline.source', { serviceCode: event.serviceCode }),
    status: index === sorted.length - 1 ? 'current' : 'complete',
  }));

  const missingStepItems: TimelineItem[] = CANONICAL_PROCESS_STEPS
    .filter((processCode) => !seenProcessCodes.has(processCode))
    .map((processCode) => (
      NON_PUBLISHING_STEPS.has(processCode)
        ? { id: `gap-${processCode}`, label: t('orders.timeline.gap', { processCode }), status: 'gap' as const }
        : { id: `future-${processCode}`, label: processCode, timestamp: t('orders.timeline.notStarted'), status: 'future' as const }
    ));

  return [...eventItems, ...missingStepItems];
}
